import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import Title from 'Components/ComponentLibrary/Atoms/Text/Title';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import RoundedButton from 'Components/ComponentLibrary/Atoms/Inputs/RoundedButton';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import { displayErrorMessage, displaySuccess } from 'Helpers/FacetNotificationManager';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import ConfirmationDialog from 'Components/UI/ConfirmationDialog';
import MetaField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import Typography from 'Components/UI/Typography';
import {
  updateHistoryList,
  useManagedAccountAllocationHistoryDispatchContext,
  useManagedAccountAllocationHistoryStateContext,
} from 'Views/Page/Content/Modal/Layouts/ManagedAccountAllocationCleanupDialog/Context';
import { Accordion, AccordionDetails, AccordionSummary } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { formatDate, fullDateTime } from 'Consts/DateFormats';
import { useRefreshSelectedAllocationHistory } from 'Hooks/useRefreshEvent';
import { loadNextResourceSet } from 'Context/PagedCollectionContext';
import TableView from 'Views/Page/Content/Modal/Layouts/ManagedAccountAllocationCleanupDialog/TableView/TableView';

const styles = theme => ({
  root: {
    minWidth: 800,
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    marginBottom: theme.spacing(2),
  },
  subtitle: {
    marginBottom: theme.spacing(2),
  },
  submit: {
    marginTop: theme.spacing(2),
    alignSelf: 'center',
  },
  form: {
    marginTop: theme.spacing(2),
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'row',
  },
  formGroupItem: {
    marginRight: theme.spacing(1),
  },
  fieldset: {
    display: 'flex',
    border: `1px solid ${theme.palette.facet.mediumGray}`,
    borderRadius: theme.radius.md,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  legend: {
    width: 'unset',
    fontSize: theme.typography.fontSize1Rem,
    color: theme.palette.facet.primaryGray,
    padding: theme.spacing(1),
    marginLeft: theme.spacing(3),
  },
  label: {
    fontSize: theme.typography.fontSizePt75Rem,
    margin: theme.spacing(1, 0),
  },
  justificationDescription: {
    textAlign: 'center',
    marginBottom: theme.spacing(2),
  },
  accordion: {
    position: 'unset',
    marginTop: theme.spacing(2),
    borderRadius: theme.radius.md,
  },
  accordingHeading: {
    fontSize: theme.typography.fontSize1Rem,
    color: theme.palette.facet.primaryGray,
  },
  accordionContent: {
    width: '100%',
  },
});

const getDeviationJustificationDescription = (justification) => {
  switch (justification) {
    case 'client-preference':
      return 'Client prefers to take more or less risk based on personal determination (incl. faith based restrictions)';
    case 'outside-assets':
      return 'Client has other assets held away that impact global allocation. overall allocation for outcome is in line.';
    case 'time-horizon-adjustment-due-to-cash-flow':
      return 'Client has assets that reduce (or liabilities that increase) the need to utilize funds from accounts outside of time horizon';
    case 'backdoor-roth':
      return 'Client\'s account is being used for Backdoor Conversions';
    default:
      return '';
  }
};

const ManagedAccountAllocationCleanupDialogContent = ({
  className,
  classes,
}) => {
  const {
    saving, resource,
  } = useMetaLayoutContext();
  const [showConfirm, setShowConfirm] = React.useState(false);
  const { clearModalCollection } = useModalContext();
  const { refreshPageContentCollection } = usePageContentContext();

  const [initialModel] = React.useState(resource.attr('selectedModel'));

  const selectedAllocationHistoryStateContext = useManagedAccountAllocationHistoryStateContext();
  const selectedAllocationHistoryDispatchContext = useManagedAccountAllocationHistoryDispatchContext();

  const selectedAllocationHistoryStateContextRef = useRef(selectedAllocationHistoryStateContext);
  selectedAllocationHistoryStateContextRef.current = selectedAllocationHistoryStateContext;

  useRefreshSelectedAllocationHistory(async () => {
    updateHistoryList(selectedAllocationHistoryDispatchContext, selectedAllocationHistoryStateContextRef.current.collection);
  }, []);

  const handleSubmit = async () => {
    try {
      await resource.submit();
      refreshPageContentCollection();
      clearModalCollection();
      displaySuccess('Saved Successfully');
    } catch (err) {
      displayErrorMessage(err, err.errors);
    }
  };

  const handleCancel = () => {
    setShowConfirm(false);
  };

  const handleConfirm = async () => {
    setShowConfirm(false);
    await handleSubmit();
  };

  const handleClick = async () => {
    if (resource.attr('selectedModel') !== initialModel) {
      setShowConfirm(true);
    } else {
      await handleSubmit();
    }
  };

  const getTableData = resources => resources?.map((allocationHistory) => ({
    date: allocationHistory?.attr('changedAt'),
    type: allocationHistory?.attr('type'),
    changedBy: allocationHistory?.selectedOptionPrompt('changedBy'),
    fromSelectedModel: allocationHistory?.attr('fromModel'),
    toSelectedModel: allocationHistory?.attr('toModel'),
    plannerNotes: allocationHistory?.attr('plannerNotes'),
    opsNotes: allocationHistory?.attr('opsNotes'),
    deviationJustification: allocationHistory?.attr('deviationJustification'),
    deviationJustificationAdditionalDetails: allocationHistory?.attr('deviationJustificationAdditionalDetails'),
    allocationHistory,
  }));

  const TABLE_COLUMNS = [
    {
      title: 'Date',
      key: 'date',
      render: data => formatDate(data.date, fullDateTime),
    },
    {
      title: 'Type',
      key: 'type',
    },
    {
      title: 'Changed By',
      key: 'changedBy',
    },
    {
      title: 'From',
      key: 'fromSelectedModel',
    },
    {
      title: 'To',
      key: 'toSelectedModel',
    },
    {
      title: 'Planner Notes',
      key: 'plannerNotes',
    },
    {
      title: 'Ops Notes',
      key: 'opsNotes',
    },
    {
      title: 'Deviation Justification',
      key: 'deviationJustification',
    },
    {
      title: 'Deviation Additional Details',
      key: 'deviationJustificationAdditionalDetails',
    },
  ];

  return (
    <>
      <Box className={classNames(classes.root, className)}>
        <Title className={classes.title} text={resource.attr('name')} />
        <fieldset className={classes.fieldset}>
          <legend className={classes.legend}>{'Account Details'}</legend>
          <MetaField attr={'householdId'} />
          <MetaField attr={'accountNumber'} />
          <MetaField attr={'balance'} />
          <MetaField attr={'objective'} />
          <MetaField attr={'timeHorizon'} />
          <MetaField attr={'isEsg'} />
          <MetaField attr={'isRetirementModel'} />
        </fieldset>
        <fieldset className={classes.fieldset}>
          <legend className={classes.legend}>{'Account Model Allocations'}</legend>
          <MetaField attr={'suggestedModel'} />
          <MetaField attr={'currentModel'} />
          <MetaField attr={'daysSinceSuggestedModelChange'} />
          <MetaField attr={'currentModelDaysOutOfSync'} />
          <MetaField attr={'assignedInOpsAgentId'} />
        </fieldset>
        <Accordion className={classes.accordion}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            id="header"
          >
            <Typography className={classes.accordingHeading}>Selected Allocation History</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TableView
              className={classes.accordionContent}
              type={'Selected Allocation History'}
              columns={TABLE_COLUMNS}
              data={getTableData(selectedAllocationHistoryStateContext.resources)}
              hasNextLink={selectedAllocationHistoryStateContext.hasNextLink}
              loading={selectedAllocationHistoryStateContext.loading}
              loadMoreItems={() => loadNextResourceSet(selectedAllocationHistoryDispatchContext, selectedAllocationHistoryStateContext)}
              forceUseMobileLayout
              showHeaderButton={false}
            />
          </AccordionDetails>
        </Accordion>
        <Box className={classes.form}>
          <Box className={classes.formGroup}>
            <MetaField className={classes.formGroupItem} attr={'selectedModel'} />
            <MetaField attr={'deviationJustification'} />
            {resource.attr('useLegacyAssetSolution') === 'true' && <MetaField style={{ marginLeft: 16 }} attr={'useLegacyAssetSolution'} />}
          </Box>
          {resource.attr('deviationJustification') && (
            <Typography className={classes.justificationDescription} color={'red'}>{getDeviationJustificationDescription(resource.attr('deviationJustification'))}</Typography>
          )}
          <Typography
            className={classes.label}
            color="primaryGray"
          >
            {resource.meta().field('deviationJustificationAdditionalDetails').attr('prompt')}
          </Typography>
          <MetaField attr={'deviationJustificationAdditionalDetails'} />
          <MetaField attr={'inOpsNotes'} />
          {resource.attr('dismissForAWeek') !== '' && <MetaField style={{ marginLeft: 16 }} attr={'dismissForAWeek'} />}
        </Box>
        <RoundedButton
          className={classes.submit}
          onClick={handleClick}
          disabled={saving}
        >
          {'Submit'}
        </RoundedButton>
      </Box>
      {showConfirm && <ConfirmationDialog
        message={'By submitting a model adjustment request you are confirming the client is aware of this change, and that they consented to the new model'}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
      />}
    </>
  );
};

ManagedAccountAllocationCleanupDialogContent.defaultProps = {
  className: '',
};

ManagedAccountAllocationCleanupDialogContent.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(ManagedAccountAllocationCleanupDialogContent);
